import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Alert,
    CircularProgress,
    Divider,
    Chip,
    Dialog,
    DialogContent,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Menu,
    MenuItem,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowBack, Edit as EditIcon, ExpandMore, Restaurant, Receipt, CardGiftcard as RewardIcon, MilitaryTech as ComplementaryIcon, Download as DownloadIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { memberService } from '../services/memberService';
import AddressMap from '../components/AddressMap';
import MemberRewardModal from '../components/MemberRewardModal';
import MemberComplementaryPointsModal from '../components/MemberComplementaryPointsModal';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

// Default grey-scale empty head avatar when no picture
const DEFAULT_AVATAR = "data:image/svg+xml," + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 120">' +
    '<ellipse cx="50" cy="42" rx="36" ry="38" fill="#9e9e9e"/>' +
    '<path d="M18 82 Q50 100 82 82 L82 120 L18 120 Z" fill="#bdbdbd"/>' +
    '</svg>'
);

const MemberDetail = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { userRoles } = useAuth();
    const [member, setMember] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [transactionSummary, setTransactionSummary] = useState(null);
    const [complementaryPoints, setComplementaryPoints] = useState([]);
    const [memberRewards, setMemberRewards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [transactionsLoading, setTransactionsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rewardModalOpen, setRewardModalOpen] = useState(false);
    const [complementaryPointsModalOpen, setComplementaryPointsModalOpen] = useState(false);
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [pictureUrl, setPictureUrl] = useState(null);
    const [pictureSize, setPictureSize] = useState(null);
    const [picturePreviewOpen, setPicturePreviewOpen] = useState(false);

    const hasSupportSuperUser = userRoles?.some(r => r.RoleName === 'Support Super User') ?? false;

    // Get member ID from location state or query params
    const memberId = location.state?.memberId || new URLSearchParams(location.search).get('id');
    const membershipNumber = location.state?.membershipNumber;
    const phone = location.state?.phone;
    
    console.log('MemberDetail received state:', location.state);
    console.log('MemberDetail query params:', location.search);
    console.log('memberId:', memberId);

    const fetchActivities = async (memberData) => {
        if (!memberData?.ID || !memberData?.MembershipNumber) return;
        try {
            setTransactionsLoading(true);
            const [txnRes, compRes, rewRes] = await Promise.all([
                memberService.getMemberTransactions(memberData.ID),
                memberService.getMemberComplementaryPoints(memberData.MembershipNumber),
                memberService.getMemberRewards(memberData.MembershipNumber),
            ]);
            setTransactions(txnRes.transactions || []);
            setTransactionSummary(txnRes.summary || null);
            setComplementaryPoints(compRes.complementaryPoints || []);
            setMemberRewards(rewRes.rewards || []);
        } catch (err) {
            console.error('Error fetching activities:', err);
        } finally {
            setTransactionsLoading(false);
        }
    };

    const fetchMemberDetail = async () => {
        try {
            setLoading(true);
            setError(null);
            setPictureUrl(null);
            setPictureSize(null);

            let searchCriteria = '';
            if (memberId) searchCriteria = memberId;
            else if (membershipNumber) searchCriteria = membershipNumber;
            else if (phone) searchCriteria = phone;

            if (!searchCriteria) {
                setError('No member information provided');
                setLoading(false);
                return;
            }

            const response = await memberService.searchMember(searchCriteria);

            if (response && response.member) {
                setMember(response.member);
                await fetchActivities(response.member);
                try {
                    const result = await memberService.getMemberPicture(response.member.ID);
                    if (result) {
                        setPictureUrl(result.url);
                        setPictureSize(result.size ?? null);
                    } else {
                        setPictureUrl(null);
                        setPictureSize(null);
                    }
                } catch (err) {
                    setPictureUrl(null);
                    setPictureSize(null);
                }
            } else {
                setError('Member not found');
            }
        } catch (err) {
            console.error('Error fetching member detail:', err);
            setError(err.response?.data?.message || 'Failed to fetch member details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMemberDetail();
    }, [memberId, membershipNumber, phone]);

    useEffect(() => {
        return () => {
            if (pictureUrl && pictureUrl.startsWith('blob:')) {
                URL.revokeObjectURL(pictureUrl);
            }
        };
    }, [pictureUrl]);

    const handleBack = () => {
        navigate('/members');
    };

    // Handler for when complementary points are successfully given
    const handleComplementaryPointsSuccess = () => {
        // Refresh member data to get updated tier and points
        fetchMemberDetail();
    };

    // Handler for when reward is successfully handed out
    const handleRewardSuccess = () => {
        fetchMemberDetail();
    };

    const exportAnchorOpen = Boolean(exportAnchorEl);
    const handleExportClick = (e) => setExportAnchorEl(e.currentTarget);
    const handleExportClose = () => setExportAnchorEl(null);

    const buildExportRows = (items) => {
        if (!items || !items.length) return [];
        return items.map((item) => {
            if (item.type === 'transaction') {
                const t = item.data;
                const dateStr = t.CloseBillDateTime ? new Date(t.CloseBillDateTime).toLocaleString() : 'Open';
                const desc = `Table: ${t.TableNo && t.TableNo.trim() ? t.TableNo : '(takeout)'} | Waiter: ${t.WaiterLoginName} | $${(parseFloat(t.BillAmount) || 0).toFixed(2)} (before tax and tip)`;
                const pts = (t.PointsEarned ?? 0);
                return { Type: 'Transaction', 'Date/Time': dateStr, Description: desc, Points: `+${pts}` };
            }
            if (item.type === 'complementary') {
                const cp = item.data;
                const dateStr = cp.HandoutDate ? new Date(cp.HandoutDate).toLocaleString() : '';
                return { Type: 'Complementary Points', 'Date/Time': dateStr, Description: cp.Description || '—', Points: `+${cp.Points ?? 0}` };
            }
            if (item.type === 'reward') {
                const r = item.data;
                const dateStr = r.RewardHandoutDate ? new Date(r.RewardHandoutDate).toLocaleString() : '';
                return { Type: 'Reward Redeem', 'Date/Time': dateStr, Description: r.Description || '—', Points: `-${r.PointsDeducted ?? 0}` };
            }
            return null;
        }).filter(Boolean);
    };

    const handleExport = async (format) => {
        handleExportClose();
        const rows = buildExportRows(activityItems);
        if (!rows.length) {
            return;
        }
        const memberName = [member?.FirstName, member?.LastName].filter(Boolean).join(' ') || 'Member';
        const membershipNumber = member?.MembershipNumber || 'N/A';
        const currentPointBalance = member?.TotalPoints ?? transactionSummary?.TotalPoints ?? 0;
        const safeName = memberName.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_') || 'Member';
        const baseName = `Member_Transactions_${safeName}_${new Date().toISOString().slice(0, 10)}`;
        setExporting(true);
        try {
            if (format === 'excel') {
                // Convert Points from text (e.g., "+150", "-5") to numeric
                const excelRows = rows.map(row => {
                    const pointsStr = row.Points || '';
                    const pointsNum = pointsStr.startsWith('+') 
                        ? parseInt(pointsStr.substring(1), 10) 
                        : pointsStr.startsWith('-') 
                            ? -parseInt(pointsStr.substring(1), 10) 
                            : parseInt(pointsStr, 10) || 0;
                    return {
                        ...row,
                        Points: pointsNum
                    };
                });
                
                // Build array of arrays with header rows first, then data
                const headerRows = [
                    ['Member Name', memberName],
                    ['Membership Number', membershipNumber],
                    ['Current Point Balance', currentPointBalance],
                    [], // Empty row
                ];
                
                // Get column headers from first row keys
                const columnHeaders = Object.keys(excelRows[0] || {});
                headerRows.push(columnHeaders);
                
                // Convert data rows to arrays
                const dataRows = excelRows.map(row => columnHeaders.map(key => row[key]));
                
                // Combine all rows
                const allRows = [...headerRows, ...dataRows];
                
                // Create worksheet from array of arrays
                const ws = XLSX.utils.aoa_to_sheet(allRows);
                
                // Set column widths: Type=20, Date/Time=25, Description=60, Points=15
                ws['!cols'] = [
                    { wch: 20 }, // Type
                    { wch: 25 }, // Date/Time
                    { wch: 60 }, // Description
                    { wch: 15 }  // Points
                ];
                
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Member Transactions');
                XLSX.writeFile(wb, `${baseName}.xlsx`);
            } else if (format === 'pdf') {
                const pdf = new jsPDF('l', 'mm', 'a4'); // 'l' for landscape
                const pageWidth = pdf.internal.pageSize.getWidth();
                const pageHeight = pdf.internal.pageSize.getHeight();
                const margin = 15;
                // Landscape A4: 297mm x 210mm, so more space for columns
                const colWidths = [30, 50, 150, 30];
                const rowHeight = 6;
                const headers = ['Type', 'Date/Time', 'Description', 'Points'];
                let y = 15;
                const addTitle = () => {
                    pdf.setFontSize(11);
                    pdf.setFont(undefined, 'bold');
                    pdf.text(`Member Transactions - ${memberName}`, margin, y);
                    y += 6;
                    pdf.setFontSize(7);
                    pdf.text(`Membership Number: ${membershipNumber}`, margin, y);
                    y += 5;
                    pdf.text(`Current Point Balance: ${currentPointBalance.toLocaleString()}`, margin, y);
                    y += 5;
                    pdf.text(`Generated ${new Date().toLocaleString()}`, margin, y);
                    y += 10;
                };
                addTitle();
                pdf.setFontSize(7);
                pdf.setFont(undefined, 'bold');
                let x = margin;
                headers.forEach((h, i) => {
                    pdf.text(h, x, y);
                    x += colWidths[i];
                });
                y += rowHeight + 1;
                pdf.line(margin, y, margin + colWidths.reduce((a, b) => a + b, 0), y);
                y += 3;
                pdf.setFontSize(7);
                pdf.setFont(undefined, 'normal');
                const maxDescLen = 80;
                rows.forEach((row) => {
                    if (y > pageHeight - 20) {
                        pdf.addPage();
                        y = 15;
                        pdf.setFontSize(7);
                        pdf.setFont(undefined, 'bold');
                        x = margin;
                        headers.forEach((h, i) => { pdf.text(h, x, y); x += colWidths[i]; });
                        y += rowHeight + 1;
                        pdf.line(margin, y, margin + colWidths.reduce((a, b) => a + b, 0), y);
                        y += 3;
                        pdf.setFontSize(7);
                        pdf.setFont(undefined, 'normal');
                    }
                    x = margin;
                    const type = (row.Type || '').length > 20 ? (row.Type || '').slice(0, 17) + '...' : (row.Type || '');
                    const dt = (row['Date/Time'] || '').length > 25 ? (row['Date/Time'] || '').slice(0, 22) + '...' : (row['Date/Time'] || '');
                    let desc = (row.Description || '');
                    if (desc.length > maxDescLen) desc = desc.slice(0, maxDescLen - 3) + '...';
                    const pts = row.Points || '';
                    pdf.text(type, x, y); x += colWidths[0];
                    pdf.text(dt, x, y); x += colWidths[1];
                    pdf.text(desc, x, y); x += colWidths[2];
                    pdf.text(pts, x, y);
                    y += rowHeight;
                });
                pdf.save(`${baseName}.pdf`);
            }
        } catch (err) {
            console.error('Export failed:', err);
        } finally {
            setExporting(false);
        }
    };

    // Merge transactions, complementary points, and rewards; sort by date desc
    const activityItems = useMemo(() => {
        const items = [];
        (transactions || []).forEach((t) => {
            const d = t.CloseBillDateTime || t.CreatedDate;
            items.push({ type: 'transaction', date: d ? new Date(d) : new Date(0), data: t });
        });
        (complementaryPoints || []).forEach((cp) => {
            items.push({ type: 'complementary', date: new Date(cp.HandoutDate), data: cp });
        });
        (memberRewards || []).forEach((r) => {
            items.push({ type: 'reward', date: new Date(r.RewardHandoutDate), data: r });
        });
        items.sort((a, b) => b.date - a.date);
        return items;
    }, [transactions, complementaryPoints, memberRewards]);

    const formatPhoneNumber = (phone) => {
        if (!phone) return '';
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 10) {
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
        }
        return phone;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const calculateAge = (dateOfBirth) => {
        if (!dateOfBirth) return '';
        const today = new Date();
        const birthDate = new Date(dateOfBirth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const parseAllergies = (allergyString) => {
        if (!allergyString) return [];
        return allergyString.split(', ').filter(allergy => allergy.trim() !== '');
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button variant="contained" onClick={handleBack}>
                    <ArrowBack sx={{ mr: 1 }} />
                    Back to Member List
                </Button>
            </Box>
        );
    }

    if (!member) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                    No member data available
                </Alert>
                <Button variant="contained" onClick={handleBack}>
                    <ArrowBack sx={{ mr: 1 }} />
                    Back to Member List
                </Button>
            </Box>
        );
    }

    const allergies = parseAllergies(member.Allergy);

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Button
                        variant="outlined"
                        onClick={handleBack}
                        sx={{ mr: 2 }}
                    >
                        <ArrowBack />
                    </Button>
                    <Typography variant="h4" component="h1" sx={{ color: 'primary.main', fontWeight: 600 }}>
                        Membership #: {member.MembershipNumber}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={() => navigate('/member-edit', { state: { member } })}
                    >
                        Edit Member
                    </Button>
                </Box>
            </Box>

            {/* Basic Information */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                    Basic Information
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 3,
                    rowGap: 2
                }}>
                    {/* Row 1: Name | Current Tier */}
                    <Typography variant="body1">
                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                            Name:
                        </Box>
                        {'  '}
                        <Box component="span" sx={{ fontWeight: 500 }}>
                            {member.FirstName} {member.LastName}
                        </Box>
                    </Typography>
                    
                    <Box>
                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                            Current Tier:
                        </Box>
                        {'  '}
                        <Chip 
                            label={member?.CurrentTier || transactionSummary?.CurrentTier || 'No Tier'} 
                            color="primary" 
                            variant="filled"
                            size="small"
                        />
                    </Box>
                    
                    {/* Row 2: Phone | Email */}
                    <Typography variant="body1">
                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                            Phone:
                        </Box>
                        {'  '}
                        <Box component="span" sx={{ fontWeight: 500 }}>
                            {formatPhoneNumber(member.Phone)}
                        </Box>
                    </Typography>
                    
                    <Typography variant="body1">
                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                            Email:
                        </Box>
                        {'  '}
                        <Box component="span" sx={{ fontWeight: 500 }}>
                            {member.Email || 'N/A'}
                        </Box>
                    </Typography>
                    
                    {/* Row 3: Age | Date of Birth */}
                    <Typography variant="body1">
                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                            Age:
                        </Box>
                        {'  '}
                        <Box component="span" sx={{ fontWeight: 500 }}>
                            {calculateAge(member.DateOfBirth)} years old
                        </Box>
                    </Typography>
                    
                    <Typography variant="body1">
                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                            Date of Birth:
                        </Box>
                        {'  '}
                        <Box component="span" sx={{ fontWeight: 500 }}>
                            {formatDate(member.DateOfBirth)}
                        </Box>
                    </Typography>
                    
                    {/* Row 4: Family Size | Notification Consent */}
                    <Typography variant="body1">
                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                            Family Size:
                        </Box>
                        {'  '}
                        <Box component="span" sx={{ fontWeight: 500 }}>
                            {member.FamilySize || 'N/A'}
                        </Box>
                    </Typography>
                    
                    <Typography variant="body1">
                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                            Notification Consent:
                        </Box>
                        {'  '}
                        <Box component="span" sx={{ fontWeight: 500 }}>
                            {member.NotificationConsent ? 'Yes' : 'No'}
                        </Box>
                    </Typography>
                </Box>
                
                {/* Allergy Section - Spans 2 columns */}
                <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                            Allergies:
                        </Box>
                        {allergies.length > 0 ? (
                            <>
                                {allergies.map((allergy, index) => (
                                    <Chip
                                        key={index}
                                        label={allergy.trim()}
                                        color="secondary"
                                        variant="outlined"
                                        size="small"
                                    />
                                ))}
                            </>
                        ) : (
                            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, ml: 1 }}>
                                No allergies recorded
                            </Box>
                        )}
                    </Box>
                </Box>
                
                {/* Other Notes Section - Spans 2 columns */}
                {member.Notes && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body1">
                            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                                Other Notes:
                            </Box>
                            {'  '}
                            <Box component="span" sx={{ fontWeight: 400, whiteSpace: 'pre-wrap' }}>
                                {member.Notes}
                            </Box>
                        </Typography>
                    </Box>
                )}
                
                {/* Member Since */}
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body1">
                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                            Member Since:
                        </Box>
                        {'  '}
                        <Box component="span" sx={{ fontWeight: 500 }}>
                            {formatDate(member.CreatedDate)}
                        </Box>
                    </Typography>
                </Box>

                {/* Member Picture (readonly) - click to enlarge */}
                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 400, mb: 1 }}>
                        Photo:
                    </Typography>
                    <Box
                        onClick={() => setPicturePreviewOpen(true)}
                        sx={{
                            width: 120,
                            height: 120,
                            borderRadius: '50%',
                            overflow: 'hidden',
                            border: '2px solid',
                            borderColor: 'divider',
                            bgcolor: 'grey.200',
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.9 },
                        }}
                    >
                        <img
                            src={pictureUrl || DEFAULT_AVATAR}
                            alt="Member"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                            }}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = DEFAULT_AVATAR;
                            }}
                        />
                    </Box>
                    {hasSupportSuperUser && pictureSize != null && (
                        <Typography variant="caption" sx={{ mt: 1, color: 'white' }}>
                            {(pictureSize / 1024).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} KB
                        </Typography>
                    )}
                </Box>
            </Paper>

            {/* Membership */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                    Membership
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 3,
                    rowGap: 2
                }}>
                    {/* Points | Points Needed for Next Tier */}
                    <Typography variant="body1">
                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                            Points:
                        </Box>
                        {'  '}
                        <Box component="span" sx={{ fontWeight: 500 }}>
                            {(member?.TotalPoints ?? transactionSummary?.TotalPoints ?? 0).toLocaleString()}
                        </Box>
                    </Typography>
                    
                    <Typography variant="body1">
                        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                            Points Needed for Next Tier:
                        </Box>
                        {'  '}
                        <Box component="span" sx={{ fontWeight: 500 }}>
                            {(member?.PointsNeededForNextTier ?? transactionSummary?.PointsNeededForNextTier ?? 0) > 0 ? 
                                (member?.PointsNeededForNextTier ?? transactionSummary?.PointsNeededForNextTier ?? 0).toLocaleString() : 
                                'Max Tier Reached'
                            }
                        </Box>
                    </Typography>
                </Box>
                
                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<ComplementaryIcon />}
                        onClick={() => setComplementaryPointsModalOpen(true)}
                    >
                        Complementary Points
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<RewardIcon />}
                        onClick={() => setRewardModalOpen(true)}
                    >
                        Rewards
                    </Button>
                </Box>
            </Paper>

            {/* Address Information */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                    Address
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {/* OpenStreetMap with Google Maps Link */}
                <AddressMap
                    street1={member.Address1}
                    street2={member.Address2}
                    city={member.City}
                    province={member.Province}
                    country={member.Country}
                    postalCode={member.PostalCode}
                />
            </Paper>

            {/* Member Transactions */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 600 }}>
                        Member Transactions
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                        onClick={handleExportClick}
                        disabled={exporting || transactionsLoading || activityItems.length === 0}
                    >
                        {exporting ? 'Exporting...' : 'Export'}
                    </Button>
                </Box>
                <Menu
                    anchorEl={exportAnchorEl}
                    open={exportAnchorOpen}
                    onClose={handleExportClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <MenuItem onClick={() => handleExport('excel')}>Excel</MenuItem>
                    <MenuItem onClick={() => handleExport('pdf')}>PDF</MenuItem>
                </Menu>
                <Divider sx={{ mb: 2 }} />
                
                {/* Total Spent Summary */}
                <Box sx={{ 
                    mb: 3, 
                    p: 2, 
                    backgroundColor: 'primary.light', 
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.contrastText' }}>
                        Total Spent:
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.contrastText' }}>
                        ${(transactions && transactions.length > 0 
                            ? transactions.reduce((sum, t) => sum + (parseFloat(t.BillAmount) || 0), 0) 
                            : 0
                        ).toFixed(2)}
                    </Typography>
                </Box>
                
                {transactionsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : activityItems.length === 0 ? (
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        No transactions, complementary points, or rewards found for this member
                    </Typography>
                ) : (
                    <Box>
                        {activityItems.map((item) => {
                            if (item.type === 'transaction') {
                                const transaction = item.data;
                                return (
                                    <Accordion key={transaction.MemberTransactionID} sx={{ mb: 1 }}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMore />}
                                            sx={{
                                                backgroundColor: 'grey.50',
                                                '&:hover': { backgroundColor: 'grey.100' },
                                                userSelect: 'text'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                <Receipt sx={{ mr: 2, color: 'primary.main' }} />
                                                <Box sx={{ flexGrow: 1 }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, userSelect: 'text' }}>
                                                        {transaction.CloseBillDateTime ?
                                                            new Date(transaction.CloseBillDateTime).toLocaleString() :
                                                            'Open Transaction'
                                                        }
                                                        {transaction.POSTransactionNo && (
                                                            <Box component="span" sx={{ userSelect: 'text', cursor: 'text' }}>
                                                                {` (POS Transaction: ${transaction.POSTransactionNo})`}
                                                            </Box>
                                                        )}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ userSelect: 'text' }}>
                                                        Table: {transaction.TableNo && transaction.TableNo.trim() ? transaction.TableNo : '(takeout)'} | Waiter: {transaction.WaiterLoginName} | ${(parseFloat(transaction.BillAmount) || 0).toFixed(2)} (before tax and tip)
                                                    </Typography>
                                                </Box>
                                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                                    {(transaction.PointsEarned ?? 0).toLocaleString()} Points
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box sx={{ pl: 2 }}>
                                                {transaction.items && transaction.items.length > 0 ? (
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Items:</Typography>
                                                        {transaction.items.map((i) => (
                                                            <Box key={i.ItemId} sx={{ mb: 1 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                                                                    <Restaurant sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                                                                    <Box sx={{ flexGrow: 1 }}>
                                                                        <Typography variant="body2">{i.Name1} {i.Name2 && `(${i.Name2})`}</Typography>
                                                                    </Box>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>${i.Amount?.toFixed(2)}</Typography>
                                                                </Box>
                                                                {i.remarks && i.remarks.length > 0 && (
                                                                    <Box sx={{ ml: 4, mt: 0.5 }}>
                                                                        {i.remarks.map((remark) => (
                                                                            <Box key={remark.RemarkId} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                                                <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1 }}>•</Typography>
                                                                                <Typography variant="caption" sx={{ flexGrow: 1 }}>{remark.Name1} {remark.Name2 && `(${remark.Name2})`}</Typography>
                                                                                {remark.Amount > 0 && (
                                                                                    <Typography variant="caption" sx={{ fontWeight: 500 }}>+${remark.Amount?.toFixed(2)}</Typography>
                                                                                )}
                                                                            </Box>
                                                                        ))}
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No items found for this transaction</Typography>
                                                )}
                                                {transaction.payments && transaction.payments.length > 0 && (
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Payments:</Typography>
                                                        {transaction.payments.map((payment) => (
                                                            <Box key={payment.PaymentId} sx={{ display: 'flex', alignItems: 'center', ml: 2, mb: 0.5 }}>
                                                                <Typography variant="body2" sx={{ flexGrow: 1 }}>{payment.PaymentMethod}</Typography>
                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>${payment.Amount?.toFixed(2)}</Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            }
                            if (item.type === 'complementary') {
                                const cp = item.data;
                                return (
                                    <Box
                                        key={`comp-${cp.ID}`}
                                        sx={{
                                            mb: 1,
                                            p: 2,
                                            backgroundColor: 'grey.50',
                                            borderRadius: 1,
                                            '&:hover': { backgroundColor: 'grey.100' },
                                            userSelect: 'text'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                            <ComplementaryIcon sx={{ mr: 2, color: 'primary.main' }} />
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, userSelect: 'text' }}>
                                                    {cp.HandoutDate ? new Date(cp.HandoutDate).toLocaleString() : ''} (Complementary Points)
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ userSelect: 'text', mt: 0.5 }}>
                                                    {cp.Description || '—'}
                                                </Typography>
                                            </Box>
                                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                                +{(cp.Points ?? 0).toLocaleString()} Points
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            }
                            if (item.type === 'reward') {
                                const r = item.data;
                                return (
                                    <Box
                                        key={`reward-${r.ID}`}
                                        sx={{
                                            mb: 1,
                                            p: 2,
                                            backgroundColor: 'grey.50',
                                            borderRadius: 1,
                                            '&:hover': { backgroundColor: 'grey.100' },
                                            userSelect: 'text'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                            <RewardIcon sx={{ mr: 2, color: 'primary.main' }} />
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, userSelect: 'text' }}>
                                                    {r.RewardHandoutDate ? new Date(r.RewardHandoutDate).toLocaleString() : ''} (Reward Redeem)
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ userSelect: 'text', mt: 0.5 }}>
                                                    {r.Description || '—'}
                                                </Typography>
                                            </Box>
                                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                                -{(r.PointsDeducted ?? 0).toLocaleString()} Points
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            }
                            return null;
                        })}
                    </Box>
                )}
            </Paper>

            {/* Member Reward Modal */}
            <MemberRewardModal
                open={rewardModalOpen}
                onClose={() => setRewardModalOpen(false)}
                membershipNumber={member?.MembershipNumber}
                memberName={`${member?.FirstName} ${member?.LastName}`}
                totalPoints={member?.TotalPoints ?? transactionSummary?.TotalPoints ?? 0}
                onSuccess={handleRewardSuccess}
            />

            {/* Picture Preview Modal */}
            <Dialog
                open={picturePreviewOpen}
                onClose={() => setPicturePreviewOpen(false)}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        maxWidth: 'min(90vw, 400px)',
                        borderRadius: 2,
                    },
                }}
            >
                <DialogContent
                    sx={{
                        p: 0,
                        overflow: 'hidden',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                    onClick={() => setPicturePreviewOpen(false)}
                >
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: 360,
                            aspectRatio: 1,
                            borderRadius: '50%',
                            overflow: 'hidden',
                            border: '2px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <img
                            src={pictureUrl || DEFAULT_AVATAR}
                            alt="Member"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                            }}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = DEFAULT_AVATAR;
                            }}
                        />
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Complementary Points Modal */}
            <MemberComplementaryPointsModal
                open={complementaryPointsModalOpen}
                onClose={() => setComplementaryPointsModalOpen(false)}
                membershipNumber={member?.MembershipNumber}
                memberName={`${member?.FirstName} ${member?.LastName}`}
                onSuccess={handleComplementaryPointsSuccess}
            />
        </Box>
    );
};

export default MemberDetail;
